/**
 * AuthProvider — client OIDC (Authorization Code + PKCE) e context di sessione.
 *
 * Design ref: "Auth flow OIDC" (Login, Authorization Code + PKCE, silent renew)
 * e "Design del frontend" (Auth state: context/provider che espone `user`,
 * `role`, token e helper `login()/logout()`).
 *
 * Il client OIDC (`oidc-client-ts`) è configurato dalla config runtime
 * (`config.json`, issuer/authority, clientId, redirect_uri, scope) — nessun
 * secret lato client (PKCE, nessun client secret nel browser). *Validates: 1.2.*
 *
 * Il ruolo applicativo AUTOREVOLE proviene dall'API (`GET /api/me`, letto da
 * MongoDB): dopo che la sessione è stabilita, il provider carica il profilo
 * applicativo (`me: UserDto`) e ne espone il `role`. Il ruolo best-effort dai
 * claim OIDC (`roleFromUser`) resta solo come FALLBACK transitorio, usato finché
 * `/api/me` non ha risposto (o se la chiamata fallisce). Precedenza del `role`
 * esposto dal context:
 *   1. `me.role` (autorevole, da `/api/me`) quando disponibile;
 *   2. altrimenti il ruolo dai claim OIDC (`roleFromUser`) come fallback.
 *
 * Questa precedenza è deliberata anche per i test: nei test non esiste un server
 * `/api/me`, quindi la fetch fallisce, `me` resta `null` e il `role` esposto
 * coincide con il claim OIDC (comportamento storico atteso dai test). Nell'app
 * reale `/api/me` risponde e fornisce il ruolo admin che vive solo in Mongo.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from 'oidc-client-ts';
import type { Role, UserDto } from '../shared/dto';
import { loadConfig, type RuntimeConfig } from '../config';

/** Ruoli applicativi noti (allineati a `Role` in `shared/dto`). */
const KNOWN_ROLES: readonly Role[] = ['admin', 'manager', 'reviewer', 'employer'];

function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (KNOWN_ROLES as readonly string[]).includes(value);
}

/**
 * Estrae un ruolo best-effort dai claim del profilo OIDC, quando presenti.
 * Il ruolo autorevole resta quello dell'API (`/api/me`); questo è solo un
 * fallback utile prima che la sessione applicativa sia caricata.
 */
function roleFromUser(user: User | null): Role | null {
  const profile = user?.profile as Record<string, unknown> | undefined;
  if (!profile) return null;
  const candidate = profile.role ?? profile.roles;
  if (isRole(candidate)) return candidate;
  if (Array.isArray(candidate)) {
    const found = candidate.find(isRole);
    if (found) return found;
  }
  return null;
}

/** Costruisce le impostazioni del `UserManager` dalla config runtime. */
function buildSettings(config: RuntimeConfig): UserManagerSettings {
  const { oidc } = config;
  return {
    authority: oidc.authority,
    client_id: oidc.clientId,
    redirect_uri: oidc.redirectUri,
    post_logout_redirect_uri: oidc.postLogoutRedirectUri,
    scope: oidc.scope,
    // Authorization Code + PKCE (nessun client secret nel browser).
    response_type: 'code',
    // Silent renew via refresh token; il token è tenuto in memoria/sessione.
    automaticSilentRenew: true,
    // I dati di sessione (user, state, code_verifier PKCE) su localStorage:
    // sopravvive al round-trip di redirect cross-origin verso l'IdP (alcuni
    // browser, es. Safari, non preservano in modo affidabile sessionStorage
    // durante il redirect), evitando che al ritorno la sessione non venga
    // ritrovata e si inneschi un loop di redirect.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
  };
}

/** API esposta dal context di autenticazione. */
export interface AuthContextValue {
  /** Profilo OIDC dell'utente autenticato, o `null` se non autenticato. */
  user: User | null;
  /** Profilo applicativo autorevole da `GET /api/me`, o `null` se non caricato. */
  me: UserDto | null;
  /**
   * Ruolo applicativo del context. Autorevole quando proviene da `me.role`
   * (`/api/me`); in attesa/errore ripiega sul claim OIDC (`roleFromUser`).
   */
  role: Role | null;
  /** `true` finché lo stato iniziale della sessione non è risolto. */
  isLoading: boolean;
  /**
   * `true` quando esiste una sessione autenticata ma il profilo autorevole
   * `/api/me` non è ancora stato risolto (né successo né errore). Le guardie di
   * rotta lo usano per attendere il ruolo autorevole prima di negare l'accesso.
   */
  isProfileLoading: boolean;
  /** `true` quando esiste una sessione valida (token non scaduto). */
  isAuthenticated: boolean;
  /** Avvia il flusso OIDC Authorization Code + PKCE (redirect). *Validates: 1.2.* */
  login: () => Promise<void>;
  /** Termina la sessione locale e reindirizza al logout dell'OIDC_Provider. */
  logout: () => Promise<void>;
  /** Restituisce l'access token corrente (o `null`) per il client API (Bearer). */
  getAccessToken: () => Promise<string | null>;
  /**
   * Ricarica il profilo applicativo `/api/me` e aggiorna `me`. Da chiamare dopo
   * un'azione che muta il profilo (es. salvataggio Credly) per riflettere subito
   * il nuovo stato senza ricaricare la pagina.
   */
  refreshMe: () => Promise<void>;
  /**
   * Completa lo scambio `code` + `code_verifier` al `/callback` e restituisce
   * l'utente autenticato. Da invocare dalla pagina di callback.
   */
  handleCallback: () => Promise<User>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  /**
   * `UserManager` opzionale, utile per i test. In produzione viene creato dalla
   * config runtime.
   */
  userManager?: UserManager;
}

export function AuthProvider({ children, userManager }: AuthProviderProps) {
  const [manager, setManager] = useState<UserManager | null>(userManager ?? null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Profilo applicativo autorevole (`/api/me`) e stato di caricamento dedicato.
  const [me, setMe] = useState<UserDto | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // Inizializza il UserManager dalla config runtime (se non iniettato).
  //
  // NB: niente ref "già avviato" a livello di modulo/istanza. Sotto React
  // StrictMode (dev) l'effetto viene eseguito → pulito → rieseguito; un guard a
  // ref lascerebbe `manager` a `null` per sempre (mai reimpostato dopo il
  // remount) bloccando l'app in loading. Qui deduplichiamo tramite lo stato
  // (`if (manager) return`) e ci appoggiamo alla Promise cache-ata di
  // `loadConfig()`, sicura da riattendere anche dopo il remount. Il flag
  // `cancelled` evita solo l'aggiornamento di stato di un effetto già smontato,
  // senza impedire la creazione del manager al montaggio successivo.
  useEffect(() => {
    if (userManager) {
      setManager(userManager);
      return;
    }
    // Manager già creato (es. remount StrictMode dopo il primo set): non
    // ricrearlo, ma non uscire dallo stato di loading — ci pensa il secondo
    // effetto quando il manager è presente.
    if (manager) return;

    let cancelled = false;
    loadConfig()
      .then((config) => {
        if (cancelled) return;
        setManager(new UserManager(buildSettings(config)));
      })
      .catch((err) => {
        if (cancelled) return;
        // Senza config non è possibile autenticare: usciamo dallo stato di loading
        // così l'app può mostrare login/errore. L'errore è tracciato in console.
        console.error('AuthProvider: impossibile inizializzare OIDC', err);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userManager, manager]);

  // Sottoscrive gli eventi del UserManager e carica lo stato iniziale.
  useEffect(() => {
    if (!manager) return;

    let cancelled = false;

    const onLoaded = (loaded: User) => {
      if (!cancelled) setUser(loaded);
    };
    const onUnloaded = () => {
      if (!cancelled) setUser(null);
    };
    const onExpired = () => {
      if (!cancelled) setUser(null);
    };

    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    manager.events.addAccessTokenExpired(onExpired);
    manager.events.addSilentRenewError(onExpired);

    manager
      .getUser()
      .then((existing) => {
        if (cancelled) return;
        setUser(existing);
      })
      .catch((err) => {
        if (!cancelled) console.error('AuthProvider: getUser fallito', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeUserUnloaded(onUnloaded);
      manager.events.removeAccessTokenExpired(onExpired);
      manager.events.removeSilentRenewError(onExpired);
    };
  }, [manager]);

  // Carica il profilo applicativo autorevole da `GET /api/me` quando la sessione
  // autenticata cambia. NB: `useApiClient` dipende da `useAuth`, quindi qui NON
  // possiamo usarlo (ciclo di dipendenze); usiamo una fetch diretta minimale.
  //
  // La base URL dell'API è risolta da `loadConfig()` (`config.apiBaseUrl`) e la
  // chiamata porta l'access token corrente come `Authorization: Bearer <...>`.
  // Qualsiasi errore (rete assente nei test, config non caricabile, risposta
  // non-ok come 401/403/500) lascia `me = null`: NON avviamo qui alcun re-login
  // per evitare loop; le pagine/guardie gestiscono l'assenza di ruolo.
  //
  // Fetch (ri)caricabile del profilo `/api/me`. Estratta da `useEffect` così da
  // poter essere richiamata on-demand via `refreshMe()` (es. dopo aver salvato
  // il profilo Credly, per riflettere subito il nuovo `credlyProfileUrl` senza
  // ricaricare la pagina). Ritorna il profilo aggiornato o `null` in caso di
  // fallimento (non fatale: si ripiega sul ruolo dei claim OIDC).
  const loadMe = useCallback(async (): Promise<UserDto | null> => {
    if (!manager) return null;
    const current = await manager.getUser();
    const token = current && !current.expired ? current.access_token : null;
    if (!token) throw new Error('access token assente');

    const config = await loadConfig();
    const base = config.apiBaseUrl.replace(/\/+$/, '');
    const res = await fetch(`${base}/api/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`GET /api/me ha risposto ${res.status}`);
    return (await res.json()) as UserDto;
  }, [manager]);

  useEffect(() => {
    const authenticated = Boolean(user && !user.expired);
    if (!authenticated || !manager) {
      // Logout, sessione scaduta o manager assente: azzera il profilo.
      setMe(null);
      setIsProfileLoading(false);
      return;
    }

    let cancelled = false;
    setIsProfileLoading(true);

    (async () => {
      try {
        const profile = await loadMe();
        if (!cancelled) setMe(profile);
      } catch (err) {
        // Fallimento non fatale: manteniamo `me = null` (il context ripiegherà
        // sul ruolo dei claim OIDC). Tracciato in console per diagnosi.
        if (!cancelled) {
          setMe(null);
          console.error('AuthProvider: caricamento di /api/me fallito', err);
        }
      } finally {
        if (!cancelled) setIsProfileLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, manager, loadMe]);

  /**
   * Ricarica on-demand il profilo applicativo `/api/me` e aggiorna `me`. Usato
   * quando un'azione dell'utente cambia il profilo (es. salvataggio del profilo
   * Credly) e vogliamo riflettere subito il nuovo stato senza ricaricare.
   */
  const refreshMe = useCallback(async (): Promise<void> => {
    try {
      const profile = await loadMe();
      setMe(profile);
    } catch (err) {
      console.error('AuthProvider: refresh di /api/me fallito', err);
    }
  }, [loadMe]);

  const login = useCallback(async () => {
    if (!manager) throw new Error('AuthProvider non inizializzato');
    await manager.signinRedirect();
  }, [manager]);

  const logout = useCallback(async () => {
    if (!manager) throw new Error('AuthProvider non inizializzato');
    setUser(null);
    setMe(null);
    await manager.signoutRedirect();
  }, [manager]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!manager) return null;
    const current = await manager.getUser();
    if (!current || current.expired) return null;
    return current.access_token ?? null;
  }, [manager]);

  const handleCallback = useCallback(async (): Promise<User> => {
    if (!manager) throw new Error('AuthProvider non inizializzato');
    const signedIn = await manager.signinRedirectCallback();
    setUser(signedIn);
    return signedIn;
  }, [manager]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      me,
      // Precedenza: ruolo autorevole da `/api/me` (`me.role`); in mancanza,
      // fallback sul claim OIDC finché il profilo non è caricato o se fallisce.
      role: me?.role ?? roleFromUser(user),
      isLoading,
      isProfileLoading,
      isAuthenticated: Boolean(user && !user.expired),
      login,
      logout,
      getAccessToken,
      handleCallback,
      refreshMe,
    }),
    [
      user,
      me,
      isLoading,
      isProfileLoading,
      login,
      logout,
      getAccessToken,
      handleCallback,
      refreshMe,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook di accesso al context di autenticazione. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth deve essere usato dentro <AuthProvider>');
  }
  return ctx;
}
